/**
 * Procedural sprites, pre-rendered once to offscreen canvases and drawn scaled. Vector
 * first; PNG art can replace any of these later without touching the renderer.
 * Each sprite is drawn in a square of SIZE px representing `metres` metres across.
 */
const SIZE = 128;

export interface Sprite {
  canvas: HTMLCanvasElement;
  /** metres the full sprite width represents */
  metres: number;
  /** fraction of the sprite height that sits at the waterline (0 top, 1 bottom) */
  waterline: number;
}

function make(metres: number, waterline: number, paint: (c: CanvasRenderingContext2D) => void): Sprite {
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const c = canvas.getContext('2d')!;
  c.save();
  paint(c);
  c.restore();
  return { canvas, metres, waterline };
}

/** a coconut, husk on, seen from slightly above: the sprite is 0.34 m across */
export function coconut(): Sprite {
  return make(0.34, 0.62, (c) => {
    const cx = SIZE / 2;
    const cy = SIZE / 2;
    // body
    const g = c.createRadialGradient(cx - 18, cy - 22, 6, cx, cy, 58);
    g.addColorStop(0, '#9c6b3a');
    g.addColorStop(0.55, '#6a4425');
    g.addColorStop(1, '#3a2312');
    c.fillStyle = g;
    c.beginPath();
    c.ellipse(cx, cy, 52, 46, 0, 0, Math.PI * 2);
    c.fill();
    // husk fibres
    c.strokeStyle = 'rgba(0,0,0,0.22)';
    c.lineWidth = 2;
    for (let i = 0; i < 9; i++) {
      const a = -0.9 + i * 0.22;
      c.beginPath();
      c.moveTo(cx + Math.cos(a) * 14, cy + Math.sin(a) * 12);
      c.quadraticCurveTo(cx + Math.cos(a) * 36 + 6, cy + Math.sin(a) * 30, cx + Math.cos(a) * 50, cy + Math.sin(a) * 44);
      c.stroke();
    }
    // the three eyes
    c.fillStyle = '#2a170b';
    for (const [dx, dy] of [[-9, -14], [8, -16], [0, -4]]) {
      c.beginPath();
      c.ellipse(cx + dx - 12, cy + dy - 8, 5, 4, 0, 0, Math.PI * 2);
      c.fill();
    }
    // sheen
    c.fillStyle = 'rgba(255,255,255,0.18)';
    c.beginPath();
    c.ellipse(cx - 22, cy - 24, 14, 8, -0.6, 0, Math.PI * 2);
    c.fill();
  });
}

/** a sea turtle at the surface, head up, seen from a boat: 1.0 m across */
export function turtle(): Sprite {
  return make(1.0, 0.7, (c) => {
    const cx = SIZE / 2;
    const cy = SIZE / 2 + 6;
    c.lineJoin = 'round';
    // flippers
    c.fillStyle = '#4e6b3a';
    for (const s of [-1, 1]) {
      c.beginPath();
      c.moveTo(cx + s * 30, cy - 4);
      c.quadraticCurveTo(cx + s * 60, cy - 26, cx + s * 58, cy - 2);
      c.quadraticCurveTo(cx + s * 44, cy + 6, cx + s * 30, cy + 6);
      c.fill();
    }
    // shell
    const g = c.createRadialGradient(cx - 10, cy - 18, 4, cx, cy, 44);
    g.addColorStop(0, '#8aa05a');
    g.addColorStop(0.6, '#5d7a3c');
    g.addColorStop(1, '#2f4522');
    c.fillStyle = g;
    c.beginPath();
    c.ellipse(cx, cy, 40, 28, 0, 0, Math.PI * 2);
    c.fill();
    // scutes
    c.strokeStyle = 'rgba(20,35,15,0.45)';
    c.lineWidth = 2;
    for (let i = -1; i <= 1; i++) {
      c.beginPath();
      c.ellipse(cx + i * 22, cy - 2, 11, 9, 0, 0, Math.PI * 2);
      c.stroke();
    }
    c.beginPath();
    c.ellipse(cx, cy - 16, 10, 6, 0, 0, Math.PI * 2);
    c.stroke();
    // head
    c.fillStyle = '#6f8a4e';
    c.beginPath();
    c.ellipse(cx, cy - 36, 11, 12, 0, 0, Math.PI * 2);
    c.fill();
    c.fillStyle = '#1b1b1b';
    c.beginPath();
    c.arc(cx - 5, cy - 40, 2.2, 0, Math.PI * 2);
    c.arc(cx + 5, cy - 40, 2.2, 0, Math.PI * 2);
    c.fill();
  });
}

/** a sailing catamaran seen side-on, sailing to the right: 12 m long, white on the haze */
export function catamaran(): Sprite {
  return make(12, 0.88, (c) => {
    c.fillStyle = '#f4f7f8';
    c.strokeStyle = '#c8d2d6';
    c.lineWidth = 1.5;
    // hulls (two, one slightly behind) and the bridge deck
    c.beginPath();
    c.moveTo(8, 108);
    c.lineTo(118, 108);
    c.lineTo(112, 116);
    c.lineTo(14, 116);
    c.closePath();
    c.fill();
    c.stroke();
    c.fillStyle = '#e4ebee';
    c.fillRect(18, 100, 92, 8);
    // mast
    c.fillStyle = '#d7dfe3';
    c.fillRect(62, 14, 3, 88);
    // mainsail and jib
    c.fillStyle = '#ffffff';
    c.beginPath();
    c.moveTo(65, 16);
    c.lineTo(66, 100);
    c.lineTo(108, 100);
    c.closePath();
    c.fill();
    c.stroke();
    c.beginPath();
    c.moveTo(62, 22);
    c.lineTo(61, 100);
    c.lineTo(22, 100);
    c.closePath();
    c.fill();
    c.stroke();
  });
}

/** a thrown rock: 0.14 m across */
export function rock(): Sprite {
  return make(0.14, 0.5, (c) => {
    const g = c.createRadialGradient(50, 46, 6, 64, 64, 50);
    g.addColorStop(0, '#9aa3a8');
    g.addColorStop(0.7, '#5d666b');
    g.addColorStop(1, '#363c40');
    c.fillStyle = g;
    c.beginPath();
    c.moveTo(22, 60);
    c.lineTo(40, 24);
    c.lineTo(84, 20);
    c.lineTo(108, 56);
    c.lineTo(96, 100);
    c.lineTo(46, 106);
    c.closePath();
    c.fill();
  });
}

/** a pineapple: 0.3 m */
export function pineapple(): Sprite {
  return make(0.3, 0.5, (c) => {
    // leaves
    c.fillStyle = '#3f8a3a';
    for (let i = -2; i <= 2; i++) {
      c.beginPath();
      c.moveTo(64, 52);
      c.quadraticCurveTo(64 + i * 18, 20, 64 + i * 26, 6);
      c.quadraticCurveTo(64 + i * 10, 24, 64, 52);
      c.fill();
    }
    // body
    const g = c.createRadialGradient(52, 64, 6, 64, 78, 50);
    g.addColorStop(0, '#f2c14e');
    g.addColorStop(0.7, '#d99a2b');
    g.addColorStop(1, '#8f5e14');
    c.fillStyle = g;
    c.beginPath();
    c.ellipse(64, 80, 34, 42, 0, 0, Math.PI * 2);
    c.fill();
    c.strokeStyle = 'rgba(90,50,10,0.45)';
    c.lineWidth = 2;
    for (let i = -3; i <= 3; i++) {
      c.beginPath();
      c.moveTo(30 + (i + 3) * 10, 46);
      c.lineTo(50 + (i + 3) * 10, 122);
      c.stroke();
      c.beginPath();
      c.moveTo(98 - (i + 3) * 10, 46);
      c.lineTo(78 - (i + 3) * 10, 122);
      c.stroke();
    }
  });
}

/** a fish: 0.35 m, silver */
export function fish(): Sprite {
  return make(0.35, 0.5, (c) => {
    const g = c.createLinearGradient(0, 40, 0, 90);
    g.addColorStop(0, '#6f8ea6');
    g.addColorStop(0.5, '#d9e4ec');
    g.addColorStop(1, '#8aa0b0');
    c.fillStyle = g;
    c.beginPath();
    c.moveTo(14, 64);
    c.quadraticCurveTo(50, 28, 96, 60);
    c.quadraticCurveTo(50, 100, 14, 64);
    c.fill();
    // tail
    c.beginPath();
    c.moveTo(94, 60);
    c.lineTo(122, 40);
    c.lineTo(116, 64);
    c.lineTo(122, 88);
    c.closePath();
    c.fill();
    c.fillStyle = '#1b1b1b';
    c.beginPath();
    c.arc(32, 58, 4, 0, Math.PI * 2);
    c.fill();
  });
}

/** a sneaker: 0.3 m */
export function shoe(): Sprite {
  return make(0.3, 0.5, (c) => {
    c.fillStyle = '#f4f4f4';
    c.beginPath();
    c.moveTo(14, 84);
    c.quadraticCurveTo(20, 46, 56, 44);
    c.lineTo(72, 44);
    c.quadraticCurveTo(84, 60, 114, 72);
    c.quadraticCurveTo(122, 84, 114, 92);
    c.lineTo(14, 92);
    c.closePath();
    c.fill();
    c.fillStyle = '#2c3e50';
    c.fillRect(14, 86, 100, 12);
    c.fillStyle = '#e63946';
    c.beginPath();
    c.moveTo(60, 48);
    c.quadraticCurveTo(80, 56, 108, 76);
    c.lineTo(100, 84);
    c.quadraticCurveTo(76, 64, 56, 58);
    c.closePath();
    c.fill();
    c.strokeStyle = '#999';
    c.lineWidth = 2;
    for (let i = 0; i < 3; i++) {
      c.beginPath();
      c.moveTo(34 + i * 10, 52 + i * 4);
      c.lineTo(48 + i * 10, 60 + i * 4);
      c.stroke();
    }
  });
}

/** a snorkeler at the surface: head, mask, snorkel, and an orange float: 1.2 m across */
export function snorkeler(): Sprite {
  return make(1.2, 0.72, (c) => {
    // float ring behind
    c.fillStyle = '#ff7a3d';
    c.beginPath();
    c.ellipse(64, 86, 50, 14, 0, 0, Math.PI * 2);
    c.fill();
    c.fillStyle = '#2b7f80';
    c.beginPath();
    c.ellipse(64, 86, 30, 7, 0, 0, Math.PI * 2);
    c.fill();
    // head
    c.fillStyle = '#d9a37a';
    c.beginPath();
    c.arc(64, 62, 22, 0, Math.PI * 2);
    c.fill();
    // mask
    c.fillStyle = '#1b2a33';
    c.beginPath();
    c.roundRect(44, 50, 40, 18, 7);
    c.fill();
    c.fillStyle = '#8fd3f4';
    c.beginPath();
    c.roundRect(48, 53, 32, 12, 5);
    c.fill();
    // snorkel
    c.strokeStyle = '#2d6cdf';
    c.lineCap = 'round';
    c.lineWidth = 6;
    c.beginPath();
    c.moveTo(84, 60);
    c.lineTo(92, 56);
    c.lineTo(92, 24);
    c.stroke();
    c.fillStyle = '#2d6cdf';
    c.beginPath();
    c.arc(92, 22, 5, 0, Math.PI * 2);
    c.fill();
  });
}

/** a shark fin with its wake, facing right: 1.6 m across */
export function sharkFin(): Sprite {
  return make(1.6, 0.8, (c) => {
    // wake
    c.strokeStyle = 'rgba(255,255,255,0.7)';
    c.lineWidth = 3;
    c.beginPath();
    c.moveTo(6, 96);
    c.quadraticCurveTo(40, 92, 60, 100);
    c.stroke();
    c.beginPath();
    c.moveTo(12, 104);
    c.quadraticCurveTo(44, 100, 66, 106);
    c.stroke();
    // fin
    const g = c.createLinearGradient(0, 30, 0, 100);
    g.addColorStop(0, '#5f6d75');
    g.addColorStop(1, '#2b353b');
    c.fillStyle = g;
    c.beginPath();
    c.moveTo(44, 100);
    c.quadraticCurveTo(50, 50, 86, 26);
    c.quadraticCurveTo(84, 70, 100, 100);
    c.closePath();
    c.fill();
    // a hint of back
    c.fillStyle = '#39454c';
    c.beginPath();
    c.ellipse(70, 100, 40, 6, 0, 0, Math.PI * 2);
    c.fill();
  });
}

/** a pelican in flight, side-on, heading right: 2.2 m wingspan */
export function pelican(): Sprite {
  return make(2.2, 0.5, (c) => {
    // wings (one up, one down-ish) in dark grey
    c.fillStyle = '#4a4f55';
    c.beginPath();
    c.moveTo(56, 66);
    c.quadraticCurveTo(30, 30, 8, 36);
    c.quadraticCurveTo(28, 52, 58, 76);
    c.closePath();
    c.fill();
    c.beginPath();
    c.moveTo(60, 72);
    c.quadraticCurveTo(84, 96, 118, 92);
    c.quadraticCurveTo(92, 76, 64, 64);
    c.closePath();
    c.fill();
    // body
    c.fillStyle = '#e9e6dc';
    c.beginPath();
    c.ellipse(64, 70, 26, 11, 0.1, 0, Math.PI * 2);
    c.fill();
    // neck and head
    c.beginPath();
    c.ellipse(86, 58, 9, 8, 0, 0, Math.PI * 2);
    c.fill();
    // the beak and pouch
    c.fillStyle = '#e4a742';
    c.beginPath();
    c.moveTo(92, 56);
    c.lineTo(124, 66);
    c.lineTo(94, 66);
    c.closePath();
    c.fill();
    c.fillStyle = '#1b1b1b';
    c.beginPath();
    c.arc(88, 56, 2, 0, Math.PI * 2);
    c.fill();
  });
}

/**
 * A sprite from an image file (a photo cut-out). Until the file loads the canvas holds `fallback`'s
 * drawing, so the game never shows a blank. `metres` and `waterline` describe the picture's framing.
 */
export function fromImage(url: string, metres: number, waterline: number, fallback: Sprite): Sprite {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 256;
  const c = canvas.getContext('2d')!;
  c.drawImage(fallback.canvas, 0, 0, 256, 256);
  const sprite: Sprite = { canvas, metres: fallback.metres, waterline: fallback.waterline };
  const img = new Image();
  img.onload = () => {
    c.clearRect(0, 0, 256, 256);
    c.drawImage(img, 0, 0, 256, 256);
    sprite.metres = metres;
    sprite.waterline = waterline;
  };
  img.src = url;
  return sprite;
}

/** a driftwood log, side on: 2.4 m long */
export function log(): Sprite {
  return make(2.4, 0.62, (c) => {
    const g = c.createLinearGradient(0, 56, 0, 92);
    g.addColorStop(0, '#9a7a56');
    g.addColorStop(0.5, '#6e5238');
    g.addColorStop(1, '#3f2d1e');
    c.fillStyle = g;
    c.beginPath();
    c.moveTo(6, 64);
    c.quadraticCurveTo(10, 54, 24, 56);
    c.lineTo(104, 58);
    c.quadraticCurveTo(122, 62, 118, 80);
    c.lineTo(20, 86);
    c.quadraticCurveTo(4, 82, 6, 64);
    c.closePath();
    c.fill();
    c.strokeStyle = 'rgba(0,0,0,0.25)';
    c.lineWidth = 1.5;
    for (let i = 0; i < 4; i++) {
      c.beginPath();
      c.moveTo(16, 62 + i * 6);
      c.quadraticCurveTo(64, 60 + i * 6, 112, 64 + i * 6);
      c.stroke();
    }
    // a stub of branch
    c.fillStyle = '#5a4028';
    c.beginPath();
    c.moveTo(90, 58);
    c.lineTo(98, 40);
    c.lineTo(104, 42);
    c.lineTo(100, 60);
    c.closePath();
    c.fill();
  });
}

/** a Whitman grad on a pool float, arms up, in navy and gold: 1.6 m across */
export function grad(): Sprite {
  return make(1.6, 0.74, (c) => {
    // the float
    c.fillStyle = '#f2c94c';
    c.beginPath();
    c.ellipse(64, 96, 52, 16, 0, 0, Math.PI * 2);
    c.fill();
    c.fillStyle = '#2b7f80';
    c.beginPath();
    c.ellipse(64, 96, 30, 8, 0, 0, Math.PI * 2);
    c.fill();
    // gown
    c.fillStyle = '#1b2a4a';
    c.beginPath();
    c.moveTo(44, 56);
    c.lineTo(84, 56);
    c.lineTo(92, 96);
    c.lineTo(36, 96);
    c.closePath();
    c.fill();
    // arms up
    c.strokeStyle = '#1b2a4a';
    c.lineCap = 'round';
    c.lineWidth = 11;
    c.beginPath();
    c.moveTo(48, 60);
    c.lineTo(26, 24);
    c.moveTo(80, 60);
    c.lineTo(102, 24);
    c.stroke();
    c.fillStyle = '#d9a37a';
    c.beginPath();
    c.arc(26, 20, 6, 0, Math.PI * 2);
    c.arc(102, 20, 6, 0, Math.PI * 2);
    c.fill();
    // head and the open mouth of someone yelling Go Blues
    c.beginPath();
    c.arc(64, 44, 13, 0, Math.PI * 2);
    c.fill();
    c.fillStyle = '#5a1a1e';
    c.beginPath();
    c.ellipse(64, 50, 4, 3, 0, 0, Math.PI * 2);
    c.fill();
    // the mortarboard
    c.fillStyle = '#1b2a4a';
    c.beginPath();
    c.moveTo(44, 32);
    c.lineTo(64, 24);
    c.lineTo(84, 32);
    c.lineTo(64, 40);
    c.closePath();
    c.fill();
    c.strokeStyle = '#f2c94c';
    c.lineWidth = 2;
    c.beginPath();
    c.moveTo(64, 32);
    c.lineTo(80, 44);
    c.stroke();
  });
}

/** the tossed mortarboard: 0.35 m */
export function cap(): Sprite {
  return make(0.35, 0.5, (c) => {
    c.fillStyle = '#1b2a4a';
    c.beginPath();
    c.moveTo(14, 60);
    c.lineTo(64, 36);
    c.lineTo(114, 60);
    c.lineTo(64, 84);
    c.closePath();
    c.fill();
    c.fillStyle = '#121d33';
    c.fillRect(44, 60, 40, 22);
    c.strokeStyle = '#f2c94c';
    c.lineWidth = 3;
    c.beginPath();
    c.moveTo(64, 60);
    c.lineTo(96, 90);
    c.stroke();
  });
}
